import { useEffect, useState } from 'react';
import { Filter } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import {
  fetchEnginConsommationTable,
  fetchEnginConsommationLineChart,
  fetchEnginConsommationBarChart,
} from '@/store/thunks/chartThunks';
import { Button, Input } from '@/components/ui';
import EnginConsommationTable from './EnginConsommationTable';
import EnginLineChart from './EnginLineChart';
import EnginBarChart from './EnginBarChart';

export default function DashboardEnginSection() {
  const dispatch = useAppDispatch();
  const tableData = useAppSelector((s: RootState) => s.chart.enginConsommationTable);
  const lineData = useAppSelector((s: RootState) => s.chart.enginConsommationLineChart);
  const barData = useAppSelector((s: RootState) => s.chart.enginConsommationBarChart);

  const [dateStart, setDateStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [dateEnd, setDateEnd] = useState(() => {
    const d = new Date();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return last.toISOString().slice(0, 10);
  });

  const loadData = () => {
    const filter = { date_start: dateStart, date_end: dateEnd };
    dispatch(fetchEnginConsommationTable(filter));
    dispatch(fetchEnginConsommationLineChart(filter));
    dispatch(fetchEnginConsommationBarChart(filter));
  };

  useEffect(() => {
    loadData();
  }, []);

  const totalGasoil = tableData.reduce((sum, row) => sum + (row.total_gasoil ?? 0), 0);
  const totalKm = tableData.reduce((sum, row) => sum + (row.total_km ?? 0), 0);
  const totalHeures = tableData.reduce((sum, row) => sum + (row.total_h ?? 0), 0);
  const enginsActifs = tableData.length;
  const consoMoyenne = enginsActifs > 0 ? totalGasoil / enginsActifs : 0;

  const formatNum = (v: number) =>
    Number.isFinite(v) ? v.toLocaleString('fr-FR', { maximumFractionDigits: 1 }) : '0';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4">
       <div className="grid grid-cols-2 gap-4 w-full">
        <Input
            label="Date début"
            type="date"
            id="engin-date-start"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
          />
          <Input
            label="Date fin"
            type="date"
            id="engin-date-end"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
          />
       </div>
        <Button onClick={loadData}>
          <Filter className="mr-2 h-4 w-4" />
          Filtrer
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Total gasoil (période)</p>
          <p className="text-xl font-semibold text-teal-800">{formatNum(totalGasoil)} L</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Engins actifs</p>
          <p className="text-xl font-semibold text-slate-800">{enginsActifs}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Cumul KM</p>
          <p className="text-xl font-semibold text-slate-800">{formatNum(totalKm)}</p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Conso moy. / engin</p>
          <p className="text-xl font-semibold text-amber-800">{formatNum(consoMoyenne)} L</p>
        </div>
        <div className="col-span-2 sm:col-span-4 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Cumul heures (engins type H)</p>
          <p className="text-xl font-semibold text-slate-800">{formatNum(totalHeures)} h</p>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Tableau des consommations</h3>
        <EnginConsommationTable data={tableData} />
      </div>

     <div className="grid grid-cols-2 gap-4">
     <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Consommations par jour</h3>
        <EnginLineChart data={lineData} />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Total consommations</h3>
        <EnginBarChart data={barData} />
      </div>
     </div>
    </div>
  );
}
